import { Link } from "react-router-dom";
import { Mail, MapPin, Globe } from "lucide-react";
import { useContent } from "@/lib/content";
import { COMPLIANCE_LINE } from "@/lib/siteContent";
import { useI18n } from "@/lib/i18n";

export default function Footer() {
    const content = useContent();
    const { t, lang } = useI18n();
    const cities = content?.cities?.list || [];
    const email =
        content?.contact?.email || "contact@xv-capitalpartners.com";
    const complianceLine = t("footer.legal", COMPLIANCE_LINE);
    const description = t("footer.description");

    return (
        <footer
            data-testid="footer"
            className="relative bg-xv-midnightDeep text-xv-ivory overflow-hidden"
        >
            <img
                src="/brand/filigrane-dark.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none select-none absolute -right-32 -bottom-20 w-[640px] opacity-[0.05]"
            />
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
                <div
                    className="w-full h-full"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 20% 10%, rgba(212,175,55,0.5) 0px, transparent 40%), radial-gradient(circle at 80% 80%, rgba(212,175,55,0.35) 0px, transparent 45%)",
                    }}
                />
            </div>

            <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                    <div className="md:col-span-5">
                        <img
                            src="/brand/logo-principal.png"
                            alt="XV Capital Partners"
                            className="h-24 w-auto mb-8 opacity-95"
                        />
                        <p className="font-serif text-2xl md:text-[26px] leading-[1.4] text-xv-ivory/90 max-w-md">
                            {description}
                        </p>
                        <div className="mt-10 flex items-center gap-4 text-xs tracking-[0.3em] uppercase text-xv-ivory/60">
                            <span className="divider-gold" />
                            {t("footer.tagline")}
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="overline text-xv-goldSoft mb-6">
                            {t("footer.navigation")}
                        </h4>
                        <ul className="space-y-3 text-[14px] tracking-wide font-sans-modern text-xv-ivory/75">
                            <li><Link to="/" data-testid="footer-link-home" className="link-underline">{t("nav.home")}</Link></li>
                            <li><Link to="/a-propos" data-testid="footer-link-about" className="link-underline">{t("nav.about")}</Link></li>
                            <li><Link to="/expertises" data-testid="footer-link-expertises" className="link-underline">{t("nav.expertises")}</Link></li>
                            <li><Link to="/approche" data-testid="footer-link-approche" className="link-underline">{t("nav.approach")}</Link></li>
                            <li><Link to="/ecosysteme" data-testid="footer-link-ecosysteme" className="link-underline">{t("nav.ecosystem")}</Link></li>
                            <li><Link to="/collectivites" data-testid="footer-link-collectivites" className="link-underline">{t("nav.collectivites")}</Link></li>
                            <li><Link to="/articles" data-testid="footer-link-articles" className="link-underline">{t("nav.articles")}</Link></li>
                            <li><Link to="/contact" data-testid="footer-link-contact" className="link-underline">{t("nav.contact")}</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <h4 className="overline text-xv-goldSoft mb-6">
                            {t("footer.contact")}
                        </h4>
                        <ul className="space-y-5 text-[14px] font-sans-modern text-xv-ivory/80">
                            <li className="flex items-start gap-3">
                                <Mail strokeWidth={1} size={16} className="mt-1 text-xv-goldSoft shrink-0" />
                                <a href={`mailto:${email}`} data-testid="footer-email" className="link-underline">{email}</a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin strokeWidth={1} size={16} className="mt-1 text-xv-goldSoft shrink-0" />
                                <span className="leading-relaxed">{cities.join(" · ")}</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <Globe strokeWidth={1} size={16} className="mt-1 text-xv-goldSoft shrink-0" />
                                <a href="https://www.xv-capitalpartners.com" data-testid="footer-website" className="link-underline" target="_blank" rel="noreferrer noopener">www.xv-capitalpartners.com</a>
                            </li>
                        </ul>
                        <Link
                            to="/contact"
                            data-testid="footer-cta"
                            className="mt-10 inline-flex items-center gap-3 px-6 py-3 text-[11px] tracking-[0.28em] uppercase border border-xv-ivory/40 text-xv-ivory hover:bg-xv-gold hover:border-xv-gold hover:text-xv-black transition-all duration-700"
                        >
                            {t("footer.requestInterview")}
                            <span className="block w-4 h-px bg-current" />
                        </Link>
                    </div>
                </div>

                <div className="mt-20 pt-10 border-t border-xv-ivory/15">
                    <p
                        data-testid="compliance-line"
                        className="text-[12px] md:text-[13px] leading-[1.85] text-xv-ivory/55 font-light max-w-4xl"
                    >
                        {complianceLine}
                    </p>
                    <div className="mt-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-[11px] tracking-[0.24em] uppercase text-xv-ivory/45">
                        <span>
                            © {new Date().getFullYear()} XV Capital Partners ·{" "}
                            {lang === "en" ? "All rights reserved" : "Tous droits réservés"}
                        </span>
                        <div className="flex items-center gap-5">
                            <Link
                                to="/mentions-legales"
                                data-testid="footer-link-mentions"
                                className="link-underline"
                            >
                                {lang === "en" ? "Legal" : "Mentions légales"}
                            </Link>
                            <Link
                                to="/confidentialite"
                                data-testid="footer-link-confidentialite"
                                className="link-underline"
                            >
                                {lang === "en" ? "Privacy" : "Confidentialité"}
                            </Link>
                            <Link
                                to="/cgu"
                                data-testid="footer-link-cgu"
                                className="link-underline"
                            >
                                {lang === "en" ? "Terms" : "CGU"}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
